import classNames from 'classnames';
import { PropsWithChildren, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import { AnalyticTag } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  LeftFilters,
  SearchLayoutContainer,
  SearchLayoutMain,
  VerticalScroll,
} from '@sorare/core/src/components/search/SearchLayout/ui';
import { AlgoliaCardIndexesNames } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import SearchCardsContextProvider from '@sorare/core/src/contexts/searchCards/Provider';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi';
import useToggle from '@sorare/core/src/hooks/useToggle';
import { FiltersGroup } from '@sorare/core/src/lib/filters';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayerDetails } from 'components/player/PlayerDrawer';
import CurrentMarketplaceUserProvider from 'contexts/currentMarketplaceUser/Provider';
import useDefaultFilters from 'hooks/useDefaultFilters';
import { ActiveFilters } from 'search/ActiveFilters';
import FiltersManager from 'search/FiltersManager';
import FiltersManagerDialog from 'search/FiltersManagerDialog';
import SearchBox from 'search/SearchBox';
import ToggleFiltersManager from 'search/ToggleFiltersManager';
import { messages } from 'searchCards/AdvancedCardSearch/SearchLayout';
import CardResultsFromGraphQL from 'searchCards/CardResultsFromGraphQL';
import { CollectionNavigationSwitch } from 'searchCards/CollectionNavigationSwitch';
import SortCards from 'searchCards/SortCards';
import { TotalResults } from 'searchCards/TotalResults';

import { CardsDropSummary } from './CardsDropSummary';
import { DropCardsButton } from './DropCardsButton';
import { useSelectingCardsToDrop } from './useCurrentRarity';

const SearchCtn = styled(Vertical).attrs({ gap: 2 })`
  padding: 0 0 var(--double-unit);
`;
const SearchRow = styled(Horizontal).attrs({ gap: 1.5 })`
  justify-content: space-between;
`;
const SortAndSegment = styled(Vertical).attrs({ gap: 1.5 })`
  @media ${laptopAndAbove} {
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-start;
  }
`;
const SortCtn = styled(Horizontal)`
  width: 100%;
  padding-bottom: var(--intermediate-unit);
  border-bottom: 1px solid rgba(var(--c-rgb-neutral-1000), 0.11);
  @media ${laptopAndAbove} {
    width: auto;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const StyledSearchLayoutMain = styled(SearchLayoutMain)`
  position: relative;
  &.selectingCardsToDrop {
    padding-top: 0;
  }
`;

type Props = {
  sport: Sport;
  analyticsTags: AnalyticTag[];
  availableSorts: AlgoliaCardIndexesNames;
  cardFilters: FiltersGroup[];
  playerDetailsComponent: PlayerDetails;
  rarity?: Rarity;
};

const MyCardsSearchContent = ({
  cardFilters,
  rarity,
  availableSorts,
  playerDetailsComponent,
  sport,
}: Pick<
  Props,
  | 'cardFilters'
  | 'rarity'
  | 'availableSorts'
  | 'playerDetailsComponent'
  | 'sport'
>) => {
  const { formatMessage } = useIntl();
  const { currentUser } = useCurrentUserContext();
  const { up: isLaptop } = useScreenSize('laptop');

  const [filtersOpen, toggleFilters] = useToggle(false);
  const [selectingCardsToDrop, setSelectingCardsToDrop] =
    useSelectingCardsToDrop(rarity);
  const [selectedCardsSlugs, setSelectedCardsSlugs] = useState<string[]>([]);

  if (!selectingCardsToDrop && selectedCardsSlugs.length > 0) {
    setSelectedCardsSlugs([]);
  }
  const { updateDOM } = useTransitionApi();
  return (
    <>
      <SearchLayoutContainer>
        {isLaptop && (
          <VerticalScroll>
            <LeftFilters visible>
              <SearchCtn>
                <SearchBox
                  withClearIcon
                  placeholder={formatMessage(messages.defaultPlaceholder)}
                />
                <SearchRow>
                  <TotalResults />
                </SearchRow>
              </SearchCtn>
              <FiltersManager filters={cardFilters} skipClearAllButton />
            </LeftFilters>
          </VerticalScroll>
        )}
        <StyledSearchLayoutMain
          className={classNames({ selectingCardsToDrop })}
        >
          <CardsDropSummary
            onCancel={() => {
              updateDOM(() => {
                setSelectingCardsToDrop(false);
              });
            }}
            selectedCardsSlugs={selectedCardsSlugs}
            canSellCommon={selectingCardsToDrop}
          />
          {!selectingCardsToDrop && (
            <>
              <SortAndSegment>
                {isLaptop && (
                  <SortCtn>
                    <SortCards indexes={availableSorts} />{' '}
                  </SortCtn>
                )}
                <SBHorizontal>
                  {!isLaptop && (
                    <div>
                      <ToggleFiltersManager
                        toggleFilters={toggleFilters}
                        color="quaternary"
                        size="small"
                      />
                    </div>
                  )}
                  <CollectionNavigationSwitch rarity={rarity} />
                </SBHorizontal>
              </SortAndSegment>
              <ActiveFilters />
            </>
          )}
          <CardResultsFromGraphQL
            hideOwner
            galleryOwnerSlug={currentUser?.slug}
            showDesktopFilter
            PlayerDetailsComponent={playerDetailsComponent}
            sport={sport}
            editableLists
            rarity={rarity}
            canSellCommon={selectingCardsToDrop}
            selectedCardsToDropSlugs={selectedCardsSlugs}
            onToggleCardToDrop={
              selectingCardsToDrop
                ? cardSlug => {
                    updateDOM(() => {
                      setSelectedCardsSlugs(prev =>
                        prev.includes(cardSlug)
                          ? prev.filter(slug => slug !== cardSlug)
                          : [...prev, cardSlug]
                      );
                    });
                  }
                : undefined
            }
          />
        </StyledSearchLayoutMain>
      </SearchLayoutContainer>
      {!isLaptop && (
        <FiltersManagerDialog
          open={filtersOpen}
          onClose={toggleFilters}
          filtersGroups={cardFilters}
          sorts={availableSorts}
        >
          <Vertical>
            <SearchBox
              withClearIcon
              placeholder={formatMessage(messages.defaultPlaceholder)}
            />
            <SearchRow>
              <TotalResults />
            </SearchRow>
          </Vertical>
        </FiltersManagerDialog>
      )}
      <DropCardsButton
        rarity={rarity}
        selectingCardsToDrop={selectingCardsToDrop}
        setSelectingCardsToDrop={setSelectingCardsToDrop}
      />
    </>
  );
};

export const MyCardsSearch = ({
  sport,
  analyticsTags,
  availableSorts,
  cardFilters,
  playerDetailsComponent,
  rarity,
  children,
}: PropsWithChildren<Props>) => {
  const { currentUser } = useCurrentUserContext();

  const defaultFilters = useDefaultFilters({
    rarity,
    userId: idFromObject(currentUser?.id),
  });

  return (
    <CurrentMarketplaceUserProvider sport={sport}>
      <SearchCardsContextProvider
        cardFilters={cardFilters}
        galleryOwnerSlug={currentUser?.slug}
        editableLists
      >
        <InstantCardSearch
          indexes={['Cards New']}
          availableSorts={availableSorts}
          analyticsTags={analyticsTags}
          sport={sport}
          defaultFilters={defaultFilters}
          urlState
        >
          <MyCardsSearchContent
            cardFilters={cardFilters}
            rarity={rarity}
            availableSorts={availableSorts}
            playerDetailsComponent={playerDetailsComponent}
            sport={sport}
          />
          {children}
        </InstantCardSearch>
      </SearchCardsContextProvider>
    </CurrentMarketplaceUserProvider>
  );
};
